import { Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { CircleXIconButton } from "@smartrr/shared/customerPortal/components/CircleXIconButton";
import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { IPurchaseStateLineItemDiscount } from "@smartrr/shared/interfaces/Discount";
import { CurrencyCode } from "@smartrr/shared/shopifyGraphQL/api";
import {
  formatAdjustmentValue,
  formatDiscountValue,
  getShippingDisplay,
} from "@smartrr/shared/utils/displayUtils";
import {
  isShippingDiscount,
  selectPricingPolicyCycleAdjustmentForCycle,
} from "@smartrr/shared/utils/getDiscounts";
import { getNextActualDeliveryForCpsGroup } from "@smartrr/shared/utils/getScheduledDeliveries";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import isInteger from 'lodash/isInteger';
import trim from 'lodash/trim';
import React, { useMemo } from "react";

import { useOrganization } from "@customer-app/account/views/OperationAquarius/utils";
import {
  formatDeliveryPrice,
  getDeliveryIndexExcludingSkipped,
  usesRemaining,
  getUpdatedDiscount,
} from "@customer-app/account/views/OperationAquarius/utils/Pricing/Delivery";

import { useGetAllSellingPlanGroups } from "../../../../../../hooks/useGetAllSellingPlanGroups";
import { LoyaltyStoreAccess } from "../../../../../Loyalty/store";
import { isUpcomingOrderCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";
import { isDiscountCodeForPrepaid } from "@smartrr/shared/utils/discountCodes";

interface IShowPriceProps {
  delivery: PurchaseStateAndDelivery;
  includeAddons: boolean;
  onDiscountCodeRemove: (code: string) => void;
  showAmountSaved: boolean;
}

export const ShowPrice = ({
  delivery,
  includeAddons,
  onDiscountCodeRemove,
  showAmountSaved,
}: IShowPriceProps) => {
  const { purchaseState, indexFromScheduleStart: skdIdx, paymentMultipleDueOnDate } = delivery;

  const unskippedDeliveryIndex = getDeliveryIndexExcludingSkipped(delivery);

  const localizeShorthand = "subscriptions.yourSubscriptions.orderOptionsModal";
  const allGroups = useGetAllSellingPlanGroups();
  const { currency } = purchaseState;

  const organization = useOrganization();

  const isSpecialReferralsEnabled = !!LoyaltyStoreAccess.useSpecialReferralsVariantId();

  const nextDelivery = getNextActualDeliveryForCpsGroup(organization, [delivery.purchaseState]);

  const isUpcomingOrder =
    nextDelivery && nextDelivery.length > 0
      ? nextDelivery[0].toISOString() === delivery.date.toISOString()
      : skdIdx === 0;

  const correspondingLineItem = purchaseState.stLineItems.find(
    item => item.pricingPolicy && !item.isAddOn && !item.isRedeemed
  );

  const pricingPolicyAdjustment = correspondingLineItem?.pricingPolicy
    ? selectPricingPolicyCycleAdjustmentForCycle(correspondingLineItem.pricingPolicy, skdIdx + 1)
    : undefined;

  const lineItems = purchaseState.stLineItems.filter(
    (item): item is IPurchaseStateLineItem & { vnt: IPurchasableVariant } => {
      return item.skdIdx === null && Boolean(item.vnt);
    }
  );

  const discounts = Object.values(
    lineItems.reduce(
      (acc, lineitem) => {
        for (const lineDiscount of lineitem.discounts) {
          const discount = getUpdatedDiscount(purchaseState.discounts, lineDiscount);

          if (discount === undefined) {
            continue;
          }
          if (discount.vendorId) {
            const usageRemaining = usesRemaining(discount);

            if (unskippedDeliveryIndex < usageRemaining) {
              acc[discount.vendorId] = discount;
            }
          }
        }
        return acc;
      },
      {} as Record<string, IPurchaseStateLineItemDiscount>
    )
  );

  const shippingDiscounts = isUpcomingOrder
    ? purchaseState.discounts.filter(discount => isShippingDiscount(discount))
    : [];

  const hasDiscounts = discounts.length > 0;

  /**
   * For Special referrals we will not be displaying discount codes nor their discounted amounts in their dedicated row
   * instead we will include the special referral discount into the overall subtotal
   */
  const formattedPrice = formatDeliveryPrice(delivery, allGroups, {
    includeAddons,
    pricingType: isSpecialReferralsEnabled ? "with_discounts" : "subtotal",
  });

  const formattedDiscountPrice = formatDeliveryPrice(delivery, allGroups, {
    includeAddons,
    pricingType: isUpcomingOrder ? "with_discounts_and_shipping" : "with_discounts",
  });

  const hasAddons = useMemo(() => {
    return delivery.purchaseState.stLineItems.some(
      line => isInteger(line.skdIdx) && line.skdIdx === delivery.indexFromScheduleStart
    );
  }, [delivery]);

  const shippingDisplay = useMemo(() => {
    if (isUpcomingOrderCPSPrepaid(paymentMultipleDueOnDate) && !hasAddons) {
      return typedLocalize("general.prepaid");
    }
    return purchaseState ? getShippingDisplay(purchaseState) : "--";
  }, [paymentMultipleDueOnDate, hasAddons, purchaseState]);

  const thingsToShow = {
    subtotal: hasDiscounts || isUpcomingOrder,
    discount: hasDiscounts && !isSpecialReferralsEnabled,
    shippingDiscount: shippingDiscounts.length > 0 && !isSpecialReferralsEnabled,
    shipping: isUpcomingOrder,
    total: true,
    saved: showAmountSaved,
    disclaimer: !isUpcomingOrder,
  };

  const canRemoveShippingDiscount = useMemo(() => {
    return thingsToShow.shippingDiscount && !isDiscountCodeForPrepaid(shippingDiscounts[0]);
  }, [thingsToShow, shippingDiscounts]);

  return (
    <Stack spacing={"10px"} sx={{ marginTop: "10px" }} className={prefixClassName("discounts-wrapper")}>
      {thingsToShow.subtotal ? (
        <Stack direction="row" justifyContent="space-between" className={prefixClassName("subtotal-line")}>
          <Typography variant="body1">{localize(`${localizeShorthand}.subtotal`)}</Typography>
          <Typography variant="body1">{formattedPrice}</Typography>
        </Stack>
      ) : undefined}

      {thingsToShow.discount ? (
        <React.Fragment>
          {discounts.map(discount => {
            const discountCodeName = trim(discount.code?.toUpperCase());
            const displayCodeName = discountCodeName.length ? `(${discountCodeName})` : "";
            const isSequentialDiscount = discount.vendorId?.includes("SEQUENTIAL_FUTURE_DISCOUNT_CODE");
            const shouldDisplayRemoveButton = !(includeAddons || isSequentialDiscount);

            return (
              <Stack direction="row" justifyContent="space-between" key={discount.vendorId}>
                <Stack
                  direction="row"
                  gap="10px"
                  className={prefixClassName("line-item-discount")}
                  data-testid="line-item-discount"
                >
                  <Typography variant="body1">{typedLocalize("general.discount")}</Typography>
                  <Typography variant="body1">
                    {displayCodeName}
                    {!!shouldDisplayRemoveButton && (
                      <CircleXIconButton onClick={() => onDiscountCodeRemove(discount.vendorId!)} />
                    )}
                  </Typography>
                </Stack>

                <Typography variant="body1">{formatDiscountValue(discount)}</Typography>
              </Stack>
            );
          })}
        </React.Fragment>
      ) : undefined}

      {thingsToShow.shippingDiscount ? (
        <Stack
          direction="row"
          className={prefixClassName("shipping-discount-line")}
          data-testid="shipping-discount-line"
        >
          <Typography variant="body1">{`(${shippingDiscounts[0].title?.toUpperCase()})`}</Typography>
          {canRemoveShippingDiscount ? (
            <CircleXIconButton onClick={() => onDiscountCodeRemove(shippingDiscounts[0].vendorId!)} />
          ) : undefined}
        </Stack>
      ) : undefined}

      {thingsToShow.shipping ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          className={prefixClassName("shipping-line")}
          data-testid="shipping-line"
        >
          <Typography variant="body1">{localize(`${localizeShorthand}.shipping`)}</Typography>
          <Typography variant="body1">{shippingDisplay}</Typography>
        </Stack>
      ) : undefined}

      {thingsToShow.total ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          className={prefixClassName("estimated-total-line")}
          data-testid="estimated-total-line"
        >
          <Typography variant="body2">{localize(`${localizeShorthand}.estimatedTotal`)}</Typography>
          <Typography variant="body2">{formattedDiscountPrice}</Typography>
        </Stack>
      ) : undefined}

      {thingsToShow.saved ? (
        <Stack direction="row" justifyContent="flex-end" className={prefixClassName("amount-saved-wrapper")}>
          <Typography variant="caption1" sx={{ opacity: "70%" }}>
            {localize(`${localizeShorthand}.savedBySubscription`, {
              amount: formatAdjustmentValue(pricingPolicyAdjustment!, currency as CurrencyCode),
            })}
          </Typography>
        </Stack>
      ) : undefined}

      {thingsToShow.disclaimer ? (
        <Stack
          direction="row"
          justifyContent="flex-end"
          className={prefixClassName("estimated-total-information")}
        >
          <Typography variant="caption1" sx={{ opacity: "70%" }}>
            {localize(`${localizeShorthand}.estimatedTotalInformation`)}
          </Typography>
        </Stack>
      ) : undefined}
    </Stack>
  );
};
